import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { CheckCircle2, Loader2, LogOut, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Textarea } from '@/components/ui/textarea';
import {
  AppraisalHeader,
  EmployeeInfoCard,
  StatusBanner,
  SectionCard,
  QuestionEditor,
  QuestionDisplay,
} from '@/pages/admin/performance/components/shared';
import { draftFromForm, formFromDraft } from '@/pages/admin/performance/components/shared/reviewDraft';
import {
  getExitInterviewDetails,
  submitExitInterview,
  saveExitInterviewDraft,
} from '@/services/performanceService';

const formatDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getExitInterviewDate = (exitInterview) =>
  exitInterview?.interview_date || exitInterview?.due_date;

const isExitInterviewViewable = (exitInterview) => {
  const interviewDate = new Date(getExitInterviewDate(exitInterview));
  if (Number.isNaN(interviewDate.getTime())) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  interviewDate.setHours(0, 0, 0, 0);

  const daysUntilInterview = Math.ceil(
    (interviewDate - today) / (1000 * 60 * 60 * 24),
  );
  return daysUntilInterview <= 31;
};

const EmployeeExitInterviewView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const backTo = location.state?.returnTo || '/employee/reviews?tab=exit-interviews';

  const [exitInterview, setExitInterview] = useState(null);
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  const [ratings, setRatings] = useState({});
  const [comments, setComments] = useState({});
  const [selectedReasons, setSelectedReasons] = useState([]);
  const [reasonOther, setReasonOther] = useState('');
  const [overallComment, setOverallComment] = useState('');
  const [accessMessage, setAccessMessage] = useState("");

  useEffect(() => {
    loadExitInterview();
  }, [id]);

  const loadExitInterview = async () => {
    try {
      setLoading(true);
      const response = await getExitInterviewDetails(id);
      const interviewData = response.data;
      const submittedData = interviewData.exit_interview;

      if (
        interviewData.status !== "Cancelled" &&
        !isExitInterviewViewable(interviewData)
      ) {
        setAccessMessage(
          "This exit interview will be available 7 days before the last working date.",
        );
        setExitInterview(null);
        setTemplate(null);
        return;
      }

      setAccessMessage("");
      setExitInterview(interviewData);
      setTemplate(interviewData.template_id);

      if (submittedData?.responses?.length) {
        setRatings(
          submittedData.responses.reduce((acc, response) => ({
            ...acc,
            [response.question_id]: response.rating_label,
          }), {}),
        );
        setComments(
          submittedData.responses.reduce((acc, response) => ({
            ...acc,
            [response.question_id]: response.comment || '',
          }), {}),
        );
      }

      setSelectedReasons(submittedData?.selected_reasons || []);
      setReasonOther(submittedData?.reason_other || '');
      setOverallComment(submittedData?.overall_comment || '');

      const savedDraft = interviewData.my_draft;
      if (savedDraft) {
        const form = formFromDraft(savedDraft, interviewData.template_id?.sections);
        const reasons = interviewData.template_id?.exitReasons || [];
        setRatings(form.ratings);
        setComments(form.comments);
        // A reason no longer on the template couldn't be seen or unticked.
        setSelectedReasons((savedDraft.selected_reasons || []).filter((reason) => reasons.includes(reason)));
        setReasonOther(savedDraft.reason_other || '');
        setOverallComment(savedDraft.overall_comment || '');
        setDraftSavedAt(savedDraft.updatedAt);
      }
    } catch (error) {
      console.error('Error fetching exit interview:', error);
      toast.error(error.response?.data?.error || 'Failed to load exit interview');
    } finally {
      setLoading(false);
    }
  };

  const sections = template?.sections || [];
  const ratingOptions = template?.ratingOptions || [
    "Strongly Disagree",
    "Disagree",
    "Neutral",
    "Agree",
    "Strongly Agree",
  ];
  const exitReasons = template?.exitReasons || [];
  const allQuestions = useMemo(
    () => sections.flatMap((section) => (
      (section.questions || []).map((question) => ({
        ...question,
        section_id: section.id,
        section_title: section.title,
      }))
    )),
    [sections],
  );
  const allQuestionIds = allQuestions.map((question) => question.id);
  const answeredCount = allQuestionIds.filter((questionId) => ratings[questionId]).length;
  const progressPct = allQuestionIds.length > 0
    ? Math.round((answeredCount / allQuestionIds.length) * 100)
    : 0;
  const isCompleted = exitInterview?.status === 'Completed';
  const isSubmitted = [
    'Submitted',
    'Self Review',
    'Approval Review',
    'Admin Review',
  ].includes(exitInterview?.status);
  const isCancelled = exitInterview?.status === 'Cancelled';
  const isReadOnly = isCompleted || isSubmitted || isCancelled;

  const handleSaveDraft = async () => {
    setSavingDraft(true);
    try {
      const response = await saveExitInterviewDraft(id, {
        responses: draftFromForm({ ratings, comments }, allQuestionIds).responses,
        selectedReasons,
        reasonOther,
        overallComment,
      });
      setDraftSavedAt(response.data?.updatedAt || new Date());
      toast.success('Draft saved. Only you can see it until you submit.');
    } catch (error) {
      toast.error(error.response?.data?.error || error.message || 'Failed to save draft');
    } finally {
      setSavingDraft(false);
    }
  };

  const handleSubmit = async () => {
    if (!allQuestionIds.every((questionId) => ratings[questionId])) {
      toast.error('Please answer all exit interview questions before submitting');
      return;
    }

    setSubmitting(true);
    try {
      const responses = allQuestions.map((question) => ({
        section_id: question.section_id,
        section_title: question.section_title,
        question_id: question.id,
        question_text: question.text,
        rating_label: ratings[question.id],
        comment: comments[question.id] || '',
      }));

      await submitExitInterview(id, {
        responses,
        selectedReasons,
        reasonOther,
        overallComment,
      });

      toast.success('Exit interview submitted successfully');
      setTimeout(() => navigate(backTo), 1000);
    } catch (error) {
      toast.error(error.response?.data?.error || error.message || 'Failed to submit exit interview');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleReason = (reason) => {
    if (isReadOnly) return;

    setSelectedReasons((prev) => (
      prev.includes(reason)
        ? prev.filter((selectedReason) => selectedReason !== reason)
        : [...prev, reason]
    ));
  };

  const handleQuestionChange = (qId, field, value) => {
    if (field === 'rating') setRatings((prev) => ({ ...prev, [qId]: value }));
    if (field === 'comment') setComments((prev) => ({ ...prev, [qId]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!exitInterview) {
    return (
      <div className="text-center py-20 text-muted-foreground">
        <p className="text-lg">{accessMessage || "Exit interview not found."}</p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => navigate(backTo)}
        >
          Back to Reviews
        </Button>
      </div>
    );
  }

  const status = exitInterview.status;

  const appraisalShape = {
    cycleName: exitInterview.exit_interview_template_name || template?.name || 'Exit Interview',
    employeeName: exitInterview.employee_name,
    employeeId: exitInterview.employee_id?.employee_id,
    department: exitInterview.department,
  };

  const employeeShape = exitInterview.employee_id && typeof exitInterview.employee_id === 'object'
    ? {
      employee_id: exitInterview.employee_id.employee_id,
      designation: exitInterview.employee_id.designation,
      department: exitInterview.employee_id.department,
      manager: exitInterview.employee_id.manager,
      lastWorkingDate: exitInterview.employee_id.last_working_date ? formatDate(exitInterview.employee_id.last_working_date) : null,
      joinDate: exitInterview.employee_id.date_of_joining
        ? formatDate(exitInterview.employee_id.date_of_joining)
        : null,
    }
    : null;

  const submitAction = isReadOnly
    ? null
    : {
      label: 'Submit Exit Interview',
      icon: Send,
      handler: handleSubmit,
      color: 'bg-primary hover:bg-primary/90',
      disabled: progressPct < 100,
    };

  const banner = isCancelled
    ? {
      title: 'Exit Interview Cancelled',
      message: exitInterview?.cancellation_reason
        ? `This exit interview has been cancelled. Reason: ${exitInterview.cancellation_reason}`
        : 'This exit interview has been cancelled. No action is required.',
      icon: 'alert',
    }
    : isCompleted
      ? {
        title: 'Exit Interview Completed',
        message: 'Your feedback has been submitted successfully.',
        icon: 'check',
      }
      : isSubmitted
        ? {
          title: 'Exit Interview Submitted',
          message: `Your feedback is waiting for ${status}.`,
          icon: 'clock',
        }
        : {
          title: 'Start Your Exit Interview',
          message: 'Rate each question and share any final feedback before submitting.',
          icon: 'alert',
        };

  return (
    <div className="max-w-7xl mx-auto pb-20">
      <AppraisalHeader
        appraisal={appraisalShape}
        status={status}
        submitAction={submitAction}
        draftAction={submitAction ? { handler: handleSaveDraft, saving: savingDraft, savedAt: draftSavedAt } : null}
        isCompleted={isCompleted}
        isEmployeeView={true}
        onBack={() => navigate(backTo)}
        submitting={submitting}
        STEPS={null}
        hidePdfExport
        template={template}
        employee={employeeShape}
      />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-3">
          <div className="lg:sticky lg:top-24 space-y-4">
            <EmployeeInfoCard
              employee={employeeShape}
              appraisal={appraisalShape}
              status={status}
            />
          </div>
        </div>

        <div className="lg:col-span-9 space-y-8">
          <StatusBanner
            title={banner.title}
            message={banner.message}
            icon={banner.icon}
          />

          {!isReadOnly && (
            <Card>
              <CardContent className="py-4 sm:py-6 space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="text-sm sm:text-base font-semibold text-foreground">
                      Exit Interview Progress
                    </h3>
                    <p className="text-xs text-muted-foreground">
                      {answeredCount} of {allQuestionIds.length} questions answered
                    </p>
                  </div>
                  <span className="text-2xl font-bold text-primary shrink-0">
                    {progressPct}%
                  </span>
                </div>
                <Progress value={progressPct} className="h-2 w-full" />
              </CardContent>
            </Card>
          )}

          {exitReasons.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <LogOut className="h-5 w-5 text-primary" />
                  Primary Reason for Leaving
                </CardTitle>
                <CardDescription>Select all that apply.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  {exitReasons.map((reason) => {
                    const isSelected = selectedReasons.includes(reason);

                    return (
                      <button
                        key={reason}
                        type="button"
                        onClick={() => toggleReason(reason)}
                        className={`w-full rounded-md border p-3 text-left text-sm transition-colors ${
                          isSelected
                            ? "border-primary bg-primary/10 text-primary"
                            : "border-border text-muted-foreground hover:border-primary/40 hover:bg-primary/5"
                        } ${isReadOnly ? "cursor-default" : ""}`}
                        disabled={isReadOnly}
                      >
                        <span className="flex items-start gap-2">
                          <span
                            className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                              isSelected
                                ? "border-primary bg-primary"
                                : "border-border"
                            }`}
                          >
                            {isSelected && (
                              <CheckCircle2 className="h-3 w-3 text-white" />
                            )}
                          </span>
                          <span className="whitespace-normal break-words">
                            {reason}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>

                {selectedReasons.includes("Other") && (
                  <div>
                    <Label className="text-xs text-muted-foreground">
                      Please specify
                    </Label>
                    <Textarea
                      value={reasonOther}
                      onChange={(event) => setReasonOther(event.target.value)}
                      disabled={isReadOnly}
                      placeholder="Describe your reason..."
                      className="mt-1 min-h-[70px]"
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {sections.map((section, sectionIndex) => (
            <SectionCard
              key={section.id || sectionIndex}
              title={section.title}
              description={section.description}
              sectionIndex={sectionIndex}
            >
              {(section.questions || []).map((question, questionIndex) => (
                isReadOnly ? (
                  <QuestionDisplay
                    key={question.id || questionIndex}
                    question={question}
                    questionIndex={questionIndex}
                    employeeResponse={
                      ratings[question.id]
                        ? {
                          rating_label: ratings[question.id],
                          rating: 0,
                          comment: comments[question.id] || '',
                        }
                        : null
                    }
                    managerResponse={null}
                    viewMode="employee"
                  />
                ) : (
                  <QuestionEditor
                    key={question.id || questionIndex}
                    question={question}
                    questionIndex={questionIndex}
                    value={{
                      rating: ratings[question.id],
                      comment: comments[question.id],
                    }}
                    onChange={handleQuestionChange}
                    ratingOptions={ratingOptions}
                    showStars={false}
                  />
                )
              ))}
            </SectionCard>
          ))}

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Additional Comments</CardTitle>
              <CardDescription>
                Any other feedback you would like to share.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Textarea
                value={overallComment}
                onChange={(event) => setOverallComment(event.target.value)}
                disabled={isReadOnly}
                placeholder="Share any additional thoughts about your experience..."
                className="min-h-[110px] resize-none text-sm"
              />
            </CardContent>
          </Card>

          {sections.length === 0 && (
            <Card className="border-amber-200 dark:border-amber-800">
              <CardContent className="py-6 text-center text-muted-foreground">
                No template found for this exit interview.
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployeeExitInterviewView;
